import {
    EmailInsightType,
    FileInsightType,
    InsightsDataFetchSource,
    convertInsightsEntrySourceEnumToName,
    MeetingInsightType,
} from 'owa-meeting-insights-types-and-flights';
import { observer } from 'mobx-react-lite';
import { KeyCodes } from '@fluentui/react/lib';
import * as React from 'react';
import {
    AttachmentDocInfoViewBase,
    AttachmentIconViewBase,
    IconSize,
} from 'owa-attachment-common-view';
import { openFileInsight, openEmailInsight } from '../helpers/insightsClickHandlers';
import { trace } from 'owa-trace';
import { getFileInsightAriaLabel, getEmailInsightAriaLabel } from '../helpers/ariaLabelHelpers';

import styles from './CompactDoc.scss';
import classNames from 'classnames';

export interface CompactDocProps {
    type: MeetingInsightType;
    insight: FileInsightType | EmailInsightType;
    meetingId: string;
    compactDocClass?: string;
    entrySource: InsightsDataFetchSource;
    ariaRole?: string;
}

const CompactDoc = observer(function CompactDoc(props: CompactDocProps) {
    const { insight, meetingId, compactDocClass, type, entrySource, ariaRole } = props;

    React.useEffect(() => {
        if (!insight) {
            trace.info(
                'Invalid ' +
                    type +
                    ' insight data from' +
                    convertInsightsEntrySourceEnumToName(entrySource)
            );
        }
    }, [insight]);

    const onClick = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) =>
            onRowClicked(ev, type, insight, meetingId, entrySource),
        [type, insight]
    );
    const onContextMenu = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) => onRowContextMenu(ev),
        []
    );
    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent<HTMLElement>) =>
            onRowKeyDown(ev, type, insight, meetingId, entrySource),
        [type, insight]
    );
    const fileName = getFileName(type, insight);
    const itemClass = getEmbeddedItemClass(type);
    return (
        <div
            role={ariaRole}
            className={classNames(styles.container, compactDocClass)}
            tabIndex={0}
            onClick={onClick}
            onContextMenu={onContextMenu}
            onKeyDown={onKeyDown}
            title={getTitle(type, insight)}>
            <AttachmentIconViewBase
                contentType={''}
                customizeStyle={styles.icon}
                fileName={fileName}
                iconSize={IconSize.extraSmall}
                isBlocked={false}
                isFolder={false}
                useCloudIcon={false}
                embeddedItemClass={itemClass}
            />
            <AttachmentDocInfoViewBase firstLineText={fileName} isShortDocView={true} />
        </div>
    );
});

export default CompactDoc;

function onRowClicked(
    evt: React.MouseEvent<HTMLDivElement>,
    type: MeetingInsightType,
    insight: FileInsightType | EmailInsightType,
    meetingId: string,
    entrySource: InsightsDataFetchSource
) {
    evt.stopPropagation();
    openInsight(type, insight, meetingId, entrySource);
}

function onRowContextMenu(ev: React.MouseEvent<HTMLDivElement>) {
    ev.stopPropagation();
    ev.preventDefault();
}

function onRowKeyDown(
    evt: React.KeyboardEvent<HTMLElement>,
    type: MeetingInsightType,
    insight: FileInsightType | EmailInsightType,
    meetingId: string,
    entrySource: InsightsDataFetchSource
) {
    if (evt.which === KeyCodes.enter) {
        openInsight(type, insight, meetingId, entrySource);
    }
}

function openInsight(
    type: MeetingInsightType,
    insight: FileInsightType | EmailInsightType,
    meetingId: string,
    entrySource: InsightsDataFetchSource
) {
    switch (type) {
        case 'Document':
            openFileInsight(
                meetingId,
                insight as FileInsightType,
                null /* readingPaneEntrySource */,
                entrySource
            );
            break;

        case 'Message':
            openEmailInsight(
                meetingId,
                insight as EmailInsightType,
                null /* readingPaneEntrySource */,
                entrySource
            );
            break;
    }
}

function getTitle(type: MeetingInsightType, insight: FileInsightType | EmailInsightType): string {
    switch (type) {
        case 'Document':
            return getFileInsightAriaLabel(insight as FileInsightType);

        case 'Message':
            return getEmailInsightAriaLabel(
                insight as EmailInsightType,
                false /* includePreview */
            );

        default:
            return null;
    }
}

function getFileName(
    type: MeetingInsightType,
    insight: FileInsightType | EmailInsightType
): string | null {
    let fileName;
    switch (type) {
        case 'Document':
            fileName = (insight as FileInsightType).fileFullName;
            break;

        case 'Message':
            fileName = (insight as EmailInsightType).subject;
            break;
    }
    return fileName;
}

function getEmbeddedItemClass(type: MeetingInsightType) {
    return type === 'Message' ? 'IPM.Note' : null;
}
